import { useState, type ReactNode } from "react";

import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, type LucideIcon } from "lucide-react";

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "js/components/ui/collapsible";
import { cn } from "lib/utils";

interface ReportSubBlockProps {
  icon: LucideIcon;
  title: string;
  /** Optional summary rendered on the right side of the header (e.g. a status badge). */
  badge?: ReactNode;
  /** Adds a bottom divider — use on every sub-block except the last one in a card. */
  divider?: boolean;
  defaultOpen?: boolean;
  children: ReactNode;
}

/**
 * A collapsible sub-block used inside a single card (design 8a). Header shows an icon,
 * title and an optional right-aligned summary; the body expands/collapses.
 */
function ReportSubBlock({
  icon: Icon,
  title,
  badge,
  divider = false,
  defaultOpen = true,
  children,
}: ReportSubBlockProps) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <Collapsible
      open={open}
      onOpenChange={setOpen}
      className={cn(divider && "border-b border-border")}
    >
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className="flex w-full items-center gap-2.5 px-4 py-3 text-left transition-colors hover:bg-muted/40"
        >
          <Icon className="size-4 shrink-0 text-slate-500" />
          <span className="flex-1 text-sm font-semibold">{title}</span>
          {badge}
          <ChevronDown
            className={cn(
              "size-4 shrink-0 text-muted-foreground transition-transform duration-200",
              open && "rotate-180",
            )}
          />
        </button>
      </CollapsibleTrigger>

      <AnimatePresence initial={false}>
        {open && (
          <CollapsibleContent forceMount asChild>
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2, ease: "easeInOut" }}
              style={{ overflow: "hidden" }}
            >
              <div className="px-4 pb-4 pt-0.5">{children}</div>
            </motion.div>
          </CollapsibleContent>
        )}
      </AnimatePresence>
    </Collapsible>
  );
}

export default ReportSubBlock;
